import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import type { EmailVerification } from '@foundershub/api-client';
import { useEmailVerifyMutation } from '@foundershub/api-client';
import { InputField, MessageBar } from '@foundershub/components';
import { getErrorMessages, getFieldError, getValidationState } from '../modules/errors';
import { useUserRequired } from '../store';

interface Props {
  onDismiss: () => void;
  onSuccess: () => void;
  verifications?: Array<EmailVerification>;
}

export function ChangeEmail({ onDismiss, onSuccess, verifications }: Props) {
  const user = useUserRequired();
  const intl = useIntl();
  const [verifyEmail, { isSuccess, isLoading, isError, error }] = useEmailVerifyMutation();
  const classes = useStyles();

  function handleVerify(event: React.FormEvent) {
    event.preventDefault();
    const email = ((event.target as HTMLFormElement).elements.namedItem('email') as HTMLInputElement).value || '';
    const openVerification = verifications?.find(({ email: verificationEmail }) => verificationEmail === email);
    const id = openVerification?.id;
    verifyEmail({ id, email });
  }

  React.useEffect(() => {
    if (isSuccess) {
      onSuccess();
    }
  }, [isSuccess]);

  return (
    <Dialog open onOpenChange={(event, data) => !data.open && onDismiss()}>
      <DialogSurface aria-describedby={undefined}>
        <form onSubmit={handleVerify}>
          <DialogBody>
            <DialogTitle>{intl.formatMessage(title)}</DialogTitle>

            <DialogContent className={classes.formFields}>
              <Text>{intl.formatMessage(description)}</Text>

              <InputField
                defaultValue={user.contact_email}
                label={intl.formatMessage(contactEmailLabel)}
                name="email"
                required
                type="email"
                validationMessage={getFieldError(error, 'email', intl)}
                validationState={getValidationState(error, 'email')}
              />

              {isError ? (
                <MessageBar type="danger">
                  {getErrorMessages(error, intl).map((value, i) => (
                    <Text key={i}>{value}</Text>
                  ))}
                </MessageBar>
              ) : null}
            </DialogContent>

            <DialogActions>
              {isLoading ? <Spinner size="small" /> : null}
              <DialogTrigger>
                <Button disabled={isLoading} appearance="secondary">
                  {intl.formatMessage(cancelLabel)}
                </Button>
              </DialogTrigger>
              <Button disabled={isLoading} type="submit" appearance="primary">
                {intl.formatMessage(nextLabel)}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}

const title = defineMessage({
  defaultMessage: 'Change contact email',
  description: 'Modal title for change contact email form',
});
const description = defineMessage({
  defaultMessage: 'We will send a verification email to your new address for confirmation.',
  description: 'description for contact email form',
});
const contactEmailLabel = defineMessage({
  defaultMessage: 'Contact email',
  description: 'contact email label',
});
const nextLabel = defineMessage({
  defaultMessage: 'Next',
  description: 'button label to verify new email address',
});
const cancelLabel = defineMessage({
  defaultMessage: 'Cancel',
  description: 'form submit cancel button',
});

const useStyles = makeStyles({
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
});
